import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams, useLocation, useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import GlobalTimeNavigatorBar from "../components/GlobalTimeNavigatorBar";
import { useGlobalTimeNavigator } from "../shared/hooks/useGlobalTimeNavigator";
import { normalizeYmd } from "../shared/utils/timeNavigatorRange";
import DuplicateSuccessMessage from "../components/DuplicateSuccessMessage";
import DetailSaveStatus from "../components/DetailSaveStatus";
import InlinePickInput from "../components/InlinePickInput";
import DateTimeFieldGroup from "../components/DateTimeFieldGroup";
import EntityPageScaffold from "../components/entity/EntityPageScaffold";
import { TableActionColumnHeader } from "../components/TableShell";
import ImportCsvPanel from "../components/entity/ImportCsvPanel";
import CrudListToolbar from "../components/entity/CrudListToolbar";
import useBlurAutoSave from "../shared/hooks/useBlurAutoSave";
import { useEntityListChrome } from "../shared/hooks/useEntityListChrome";
import { parseOptionalInt } from "../shared/utils/csvNumber";
import { parseCsvTextToNormalizedTable, findCsvColumnIndex } from "../shared/utils/csvParse";
import { attachValidatedCsvFileImport, downloadCsvTextFile } from "../shared/utils/csvFileHelpers";
import { formatBatchSummaryMessage } from "../shared/utils/batchSummaryMessage";
import { sortRowsByConfig } from "../shared/utils/sortRowsByConfig";
import { bulkSequentialDelete } from "../shared/utils/bulkSequentialDelete";
import {
  formatSelectedCountSuffix,
  intersectSelectedIdsWithRows,
} from "../shared/utils/entityListSelection";
import {
  IconEdit,
  IconDuplicate,
  IconDelete,
  IconClose,
  IconReopen,
  IconParticipants,
  IconCancel,
  iconBtnStyle, RowActionIcons,
} from "../shared/icons/IconLib";
import { getRepeatLabel } from "../shared/utils/repeatOptions";
import RepeatPatternPicker from "../components/RepeatPatternPicker";
import { normalizeRepeatRule } from "../shared/utils/recurrence";
import {
  getDueDatePart,
  getDueTimePart,
  getDueDateForApi,
  buildTimeOptionsForWorkingStart,
} from "../shared/utils/dueDateTime";
import { validateDate, validateStartEndRange } from "../shared/utils/validation";
import { buildDuplicateLabel } from "../shared/utils/duplicateLabel";
import { useEffectiveWorkingHours } from "../shared/hooks/useAccountWorkingHours";
import { volunteerOpportunityRequestBody } from "../shared/api/volunteerOpportunityContext";
import VolunteerSectionTabs, {
  VOLUNTEER_TAB_OPPORTUNITIES,
  VOLUNTEER_TAB_SIGNUP,
} from "../components/VolunteerSectionTabs";
import { useAuth } from "../contexts/AuthContext";
import { getVolunteerOpportunityDraftFromEvent } from "../shared/utils/volunteerOpportunityFromEvent";
import { buildEventsManagementPath, EVENT_MGMT_TAB_EVENTS } from "../shared/utils/eventManagementTabs";
import {
  formatOpportunityDateTimeRange,
  formatOpportunityDatetime,
  formatVolunteerDrillStatusLabel,
  getOpportunitySignupCount,
  getOpportunityVolunteersNeeded,
  normalizeVolunteerOpportunityStatus,
  VOLUNTEER_OPPORTUNITY_STATUS_OPTIONS,
  volunteerDrillChipStyle,
  volunteerDrillClearBtnStyle,
} from "../shared/utils/volunteerOpportunityDisplay";
import {
  matchesVolunteerOpportunityScopeFilters,
  matchesVolunteerOpportunitySearch,
} from "../shared/utils/volunteerListFilters";
import { buildVolunteerParticipantsDrillFromOpportunities } from "../shared/utils/volunteerOpportunityNavigation";
import {
  VolunteerOpportunityDetailTabBar,
  VolunteerOpportunitySignupsTab,
  VO_DETAIL_TAB_DETAILS,
  VO_DETAIL_TAB_VOLUNTEERS,
} from "../components/VolunteerOpportunityDetailsTabs";
import { apiFetch } from "../shared/api/apiFetch";
const uiConfig = require("../uiConfig");
const { getMessageForFetchError } = require("../shared/utils/apiError");

const SORT_KEY_TYPES = {
  repeat_rule: "string",
  start_datetime: "date",
  end_datetime: "date",
  signup_count: "number",
  volunteers_needed: "number",
};

const OPPORTUNITY_CATEGORIES = [
  "",
  "Fundraising",
  "Hospital volunteer",
  "Hospice care",
  "Food banks",
  "Job Shadowing",
  "Tutoring underserved",
  "Crisis hotline",
  "Special needs support",
  "Community outreach",
  "Research",
  "Leadership",
  "Administrative help",
  "Event coordination",
  "Environmental",
  "Other",
];

/** Layout-only overrides for TimeSelect in panel rows (typography from TimeSelect / page.css). */
const DATE_TIME_CONTROL_LAYOUT = { minWidth: 0 };

function formatDateTimeRangeDisplay(eventDate, startTime, endTime) {
  const dateStr = (eventDate || "").trim();
  if (!dateStr) return "—";
  const dateLabel = dayjs(dateStr).isValid() ? dayjs(dateStr).format("MM/DD/YYYY") : dateStr;
  const startIso = getDueDateForApi(dateStr, startTime || "09:00");
  const endIso = getDueDateForApi(dateStr, endTime || "10:00");
  const startT = dayjs(startIso).isValid() ? dayjs(startIso).format("h:mm A") : "—";
  const endT = dayjs(endIso).isValid() ? dayjs(endIso).format("h:mm A") : "—";
  return `${dateLabel} · ${startT} – ${endT}`;
}

function normalizeStatus(value) {
  return normalizeVolunteerOpportunityStatus(value);
}

/** Card left-edge accent: Open = green, Closed = gray (matches Task card modifiers). */
function getOpportunityCardClass(status) {
  return normalizeStatus(status) === "Closed" ? "card card--closed" : "card card--priority-low";
}

/** Date + start/end time row (same layout as EventForm `/Events/new`). */
function DetailDateTimeFields({
  detailMode,
  detailActiveField,
  setDetailActiveField,
  detailEventDate,
  setDetailEventDate,
  detailStartTime,
  setDetailStartTime,
  detailEndTime,
  setDetailEndTime,
  detailRepeatRule,
  setDetailRepeatRule,
  opportunityTimeOptions,
  detailDateInputRef,
  detailFieldBlurHandler,
  detailFieldCommitKeyDownHandler,
}) {
  const editing = detailMode === "new" || detailActiveField === "dateTime";
  const commitDateTimeGroupOnEnter = (e) => {
    if (e.key === "Enter") e.stopPropagation();
    detailFieldCommitKeyDownHandler?.(e);
  };
  return (
    <div style={{ gridColumn: "1 / span 2", display: "flex", flexDirection: "column" }}>
      <label className="panel-label">Date &amp; time</label>
      {editing ? (
        <DateTimeFieldGroup
          layout="form-row"
          date={detailEventDate}
          onDateChange={setDetailEventDate}
          startTime={detailStartTime}
          onStartTimeChange={setDetailStartTime}
          endTime={detailEndTime}
          onEndTimeChange={setDetailEndTime}
          timeOptions={opportunityTimeOptions}
          onBlur={detailFieldBlurHandler}
          onKeyDown={commitDateTimeGroupOnEnter}
          dateInputRef={detailDateInputRef}
          showDatePickerOnClick
          timeSelectStyle={{ ...DATE_TIME_CONTROL_LAYOUT, flex: "1 1 88px", minWidth: 88, width: "auto" }}
        >
          {detailMode === "new" ? (
            <div className="form-datetime-row__repeat">
              <span className="panel-inline-label">Repeat</span>
              <RepeatPatternPicker
                anchorDate={detailEventDate}
                value={detailRepeatRule}
                onChange={setDetailRepeatRule}
                onBlur={detailFieldBlurHandler}
                compact
                dropdownPortal
              />
            </div>
          ) : null}
        </DateTimeFieldGroup>
      ) : (
        <button
          type="button"
          onClick={() => setDetailActiveField("dateTime")}
          className="btn-link"
          style={{ textAlign: "left", width: "100%", padding: "6px 0", color: "#212529", textDecoration: "none", fontWeight: 500 }}
        >
          {formatDateTimeRangeDisplay(detailEventDate, detailStartTime, detailEndTime)}
        </button>
      )}
    </div>
  );
}

/** Read-only opportunity fields for signup browse (no inline edit). */
function SignupBrowseDetailFields({
  detailName,
  detailCategory,
  detailStatus,
  detailRepeatRule,
  detailEventDate,
  detailStartTime,
  detailEndTime,
  detailLocation,
  detailDescription,
  detailVolunteersNeeded,
  linkedEventName,
  detailLinkedEventId,
  canViewLinkedEvent,
  navigate,
}) {
  return (
    <div style={{ padding: "12px 16px" }}>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: "12px 16px" }}>
        <div style={{ display: "flex", flexDirection: "column" }}>
          <span className="panel-label">Name</span>
          <span style={{ padding: "6px 0", fontWeight: 500 }}>{detailName || "—"}</span>
        </div>
        <div style={{ display: "flex", flexDirection: "column" }}>
          <span className="panel-label">Category</span>
          <span style={{ padding: "6px 0", fontWeight: 500 }}>{detailCategory || "—"}</span>
        </div>
        <div style={{ display: "flex", flexDirection: "column" }}>
          <span className="panel-label">Status</span>
          <span style={{ padding: "6px 0", fontWeight: 500 }}>{detailStatus || "—"}</span>
        </div>
        <div style={{ display: "flex", flexDirection: "column" }}>
          <span className="panel-label">Repeat</span>
          <span style={{ padding: "6px 0", fontWeight: 500 }}>{getRepeatLabel(detailRepeatRule)}</span>
        </div>
        <div style={{ display: "flex", flexDirection: "column" }}>
          <span className="panel-label">Volunteers needed</span>
          <span style={{ padding: "6px 0", fontWeight: 500 }}>{getOpportunityVolunteersNeeded({ volunteers_needed: detailVolunteersNeeded })}</span>
        </div>
        <div style={{ gridColumn: "1 / span 2", display: "flex", flexDirection: "column" }}>
          <span className="panel-label">Date &amp; time</span>
          <span style={{ padding: "6px 0", fontWeight: 500 }}>
            {formatDateTimeRangeDisplay(detailEventDate, detailStartTime, detailEndTime)}
          </span>
        </div>
        {(detailLinkedEventId || "").trim() && canViewLinkedEvent ? (
          <div style={{ gridColumn: "1 / span 2", display: "flex", flexDirection: "column" }}>
            <span className="panel-label">Linked event</span>
            <button
              type="button"
              className="btn-link"
              style={{ textAlign: "left", width: "100%", padding: "6px 0", color: "#0d6efd", textDecoration: "none", fontWeight: 500 }}
              onClick={() =>
                navigate(
                  buildEventsManagementPath({
                    tab: EVENT_MGMT_TAB_EVENTS,
                    detail: (detailLinkedEventId || "").trim(),
                  })
                )
              }
            >
              {linkedEventName || "View event"}
            </button>
          </div>
        ) : null}
        <div style={{ gridColumn: "1 / span 2", display: "flex", flexDirection: "column" }}>
          <span className="panel-label">Location</span>
          <span style={{ padding: "6px 0", fontWeight: 500 }}>{detailLocation || "—"}</span>
        </div>
        <div style={{ gridColumn: "1 / span 2", display: "flex", flexDirection: "column" }}>
          <span className="panel-label">Description</span>
          <span style={{ padding: "6px 0", whiteSpace: "pre-wrap" }}>{detailDescription || "—"}</span>
        </div>
      </div>
    </div>
  );
}

function VolunteerOpportunities({ signupBrowseMode = false }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { hasPermission } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const eventIdFromUrl = (searchParams.get("eventId") || "").trim();
  const categoryFromUrl = (searchParams.get("category") || "").trim();
  const statusFromUrl = (searchParams.get("status") || "").trim().toLowerCase();
  const drillSourceFromUrl = (searchParams.get("drillSource") || "").trim();
  const navViewFromUrl = (searchParams.get("navView") || "").trim().toLowerCase();
  const navDateFromUrl = (searchParams.get("navDate") || "").trim();

  const [currentObjs, setCurrentObjs] = useState([]);
  const userInfo = JSON.parse(sessionStorage.getItem("userInfo"));
  const account_id = userInfo?.account_id;
  const user_id = userInfo?._id;
  const role = userInfo?.role;
  const volunteerSession = { account_id, user_id, role };
  const serverUrl = uiConfig.serverInfo.serverUrl;
  const [statusFilter, setStatusFilter] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  /** Volunteer Signup only: "" | "available" | "registered" */
  const [signupQuickFilter, setSignupQuickFilter] = useState(() => {
    if (!signupBrowseMode) return "";
    if (drillSourceFromUrl === "volunteer_impact_dashboard") return "";
    return "available";
  });
  const timeNav = useGlobalTimeNavigator({
    initialView: ["day", "week", "month", "future", "all"].includes(navViewFromUrl) ? navViewFromUrl : "all",
    initialFocusDateYmd: navDateFromUrl || undefined,
  });

  useEffect(() => {
    if (drillSourceFromUrl !== "volunteer_impact_dashboard") return;
    if (statusFromUrl === "open") setStatusFilter("Open");
    else if (statusFromUrl === "closed") setStatusFilter("Closed");
    else setStatusFilter("");
    setCategoryFilter(categoryFromUrl || "");
    if (["day", "week", "month", "future", "all"].includes(navViewFromUrl)) {
      timeNav.setView(navViewFromUrl);
    }
    if (navDateFromUrl) timeNav.setFocusDateYmd(navDateFromUrl);
    setSignupQuickFilter("");
    // eslint-disable-next-line react-hooks/exhaustive-deps -- apply dashboard drill query params only
  }, [drillSourceFromUrl, categoryFromUrl, statusFromUrl, navViewFromUrl, navDateFromUrl]);

  function clearDashboardDrillFilters() {
    setStatusFilter("");
    setCategoryFilter("");
    timeNav.goToday();
    setSearchParams({}, { replace: true });
    if (signupBrowseMode) setSignupQuickFilter("available");
  }

  const {
    selectedIds,
    setSelectedIds,
    deleting,
    setDeleting,
    importing,
    setImporting,
    importPanelExpanded,
    setImportPanelExpanded,
    searchTerm,
    setSearchTerm,
    sortConfig,
    bannerError,
    setBannerError,
    viewMode,
    setViewMode,
    confirmDialog,
    setConfirmDialog,
    closeConfirmDialog,
    handleSort,
    handleSelectAll,
    handleSelectOne,
  } = useEntityListChrome({ initialViewMode: "card" });

  const [detailId, setDetailId] = useState(null);
  const [detailMode, setDetailMode] = useState("");
  const [detailName, setDetailName] = useState("");
  const [detailDescription, setDetailDescription] = useState("");
  const [detailEventDate, setDetailEventDate] = useState(() => dayjs().format("YYYY-MM-DD"));
  const [detailStartTime, setDetailStartTime] = useState("09:00");
  const [detailEndTime, setDetailEndTime] = useState("10:00");
  const detailDateInputRef = useRef(null);
  const { workingHoursStart, workingHoursEnd } = useEffectiveWorkingHours();
  const opportunityTimeOptions = useMemo(
    () => buildTimeOptionsForWorkingStart(workingHoursStart),
    [workingHoursStart]
  );
  const [detailRepeatRule, setDetailRepeatRule] = useState(null);
  const [detailLocation, setDetailLocation] = useState("");
  const [detailCategory, setDetailCategory] = useState("");
  const [detailVolunteersNeeded, setDetailVolunteersNeeded] = useState("1");
  const [detailStatus, setDetailStatus] = useState("Open");
  const [detailLinkedEventId, setDetailLinkedEventId] = useState("");
  const [linkedEventName, setLinkedEventName] = useState("");
  const [eventFilterName, setEventFilterName] = useState("");
  const [detailActiveField, setDetailActiveField] = useState("");
  const [detailInitialValues, setDetailInitialValues] = useState(null);
  const [opportunityStatusActionIds, setOpportunityStatusActionIds] = useState(() => new Set());
  const [signedUpOpportunityIds, setSignedUpOpportunityIds] = useState(() => new Set());
  const [detailTab, setDetailTab] = useState(VO_DETAIL_TAB_DETAILS);
  const [opportunitySignups, setOpportunitySignups] = useState([]);
  const [signupsLoading, setSignupsLoading] = useState(false);
  const [signupSubmitting, setSignupSubmitting] = useState(false);
  const createFromEventHandledRef = useRef(false);
  const openedDetailFromUrlRef = useRef("");

  function showAlertDialog(title, message) {
    setConfirmDialog({ open: true, title, message, mode: "alert", onConfirm: null });
  }

  function getOpportunityForStatusAction(id) {
    const fromList = (currentObjs || []).find((o) => String(o._id) === String(id));
    if (fromList) return fromList;
    if (detailId && String(detailId) === String(id) && detailMode !== "new") {
      const dt = buildApiDatetimes(detailEventDate, detailStartTime, detailEndTime);
      if (!dt.ok) return null;
      return {
        _id: detailId,
        name: detailName,
        description: detailDescription,
        start_datetime: dt.start_datetime,
        end_datetime: dt.end_datetime,
        repeat_rule: normalizeRepeatRule(detailRepeatRule, detailEventDate),
        location: detailLocation,
        category: detailCategory,
        status: detailStatus,
        volunteers_needed: detailVolunteersNeeded,
        event_id: detailLinkedEventId,
      };
    }
    return null;
  }

  function syncDetailAfterStatusChange(id, status) {
    if (detailId && String(detailId) === String(id) && detailMode !== "new") {
      setDetailStatus(status);
      setDetailInitialValues((prev) => (prev ? { ...prev, status: normalizeStatus(status) } : prev));
    }
  }

  function getCategories() {
    const set = new Set();
    (OPPORTUNITY_CATEGORIES || []).forEach((c) => {
      const v = (c != null ? String(c) : "").trim();
      if (v) set.add(v);
    });
    (currentObjs || []).forEach((o) => {
      const c = (o?.category != null ? String(o.category) : "").trim();
      if (c) set.add(c);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }

  useEffect(() => {
    getDocument();
    if (signupBrowseMode) loadMySignupIds();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run once on mount
  }, []);

  useEffect(() => {
    if (signupBrowseMode || !detailId || detailMode === "new") {
      setOpportunitySignups([]);
      return;
    }
    loadOpportunitySignups(detailId);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reload when opened opportunity changes
  }, [detailId, detailMode]);

  useEffect(() => {
    if (signupBrowseMode) return;
    const ev = location.state?.createFromEvent;
    if (!ev || createFromEventHandledRef.current) return;
    createFromEventHandledRef.current = true;
    const draft = getVolunteerOpportunityDraftFromEvent(ev);
    focusTimeNavOnOpportunityStart(draft.eventDate);
    openDetail(null, "new", { initialValues: draft });
    navigate(location.pathname, { replace: true, state: {} });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- one-shot create-from-event navigation
  }, [location.state?.createFromEvent]);

  useEffect(() => {
    const detailIdFromUrl = (searchParams.get("detail") || "").trim();
    if (!detailIdFromUrl) {
      openedDetailFromUrlRef.current = "";
      return;
    }
    if (openedDetailFromUrlRef.current === detailIdFromUrl) return;
    const match = (currentObjs || []).find((o) => String(o._id) === detailIdFromUrl);
    if (!match) return;
    openedDetailFromUrlRef.current = detailIdFromUrl;
    focusTimeNavOnOpportunityStart(match.start_datetime);
    openDetail(match, "view");
    const next = new URLSearchParams(searchParams);
    next.delete("detail");
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- open once when list contains target
  }, [currentObjs, searchParams]);

  useEffect(() => {
    const eid = (detailLinkedEventId || "").trim();
    if (!eid || !account_id) {
      setLinkedEventName("");
      return;
    }
    let cancelled = false;
    apiFetch(serverUrl + "/getEvent", {
      method: "POST",
      body: JSON.stringify({ account_id, _id: eid }),
    })
      .then((resp) => (resp.ok ? resp.json() : null))
      .then((data) => {
        if (!cancelled) setLinkedEventName(data?.name != null ? String(data.name).trim() : "");
      })
      .catch(() => {
        if (!cancelled) setLinkedEventName("");
      });
    return () => {
      cancelled = true;
    };
  }, [detailLinkedEventId, account_id, serverUrl]);

  useEffect(() => {
    if (!eventIdFromUrl || !account_id) {
      setEventFilterName("");
      return;
    }
    let cancelled = false;
    apiFetch(serverUrl + "/getEvent", {
      method: "POST",
      body: JSON.stringify({ account_id, _id: eventIdFromUrl }),
    })
      .then((resp) => (resp.ok ? resp.json() : null))
      .then((data) => {
        if (!cancelled) setEventFilterName(data?.name != null ? String(data.name).trim() : "");
      })
      .catch(() => {
        if (!cancelled) setEventFilterName("");
      });
    return () => {
      cancelled = true;
    };
  }, [eventIdFromUrl, account_id, serverUrl]);

  async function getDocument() {
    if (!account_id) return;
    try {
      const resp = await apiFetch(serverUrl + "/listVolunteerOpportunities", {
        method: "POST",
        body: JSON.stringify(volunteerOpportunityRequestBody(volunteerSession)),
      });
      const data = await resp.json();
      setCurrentObjs(Array.isArray(data) ? data : []);
    } catch (err) {
      setBannerError(getMessageForFetchError(err));
    }
  }

  async function loadMySignupIds() {
    if (!account_id || !user_id) {
      setSignedUpOpportunityIds(new Set());
      return;
    }
    try {
      const resp = await apiFetch(serverUrl + "/getMyVolunteerOpportunitySignupIds", {
        method: "POST",
        body: JSON.stringify(volunteerOpportunityRequestBody(volunteerSession)),
      });
      if (!resp.ok) return;
      const data = await resp.json();
      const ids = Array.isArray(data?.opportunityIds) ? data.opportunityIds : [];
      setSignedUpOpportunityIds(new Set(ids.map((id) => String(id))));
    } catch {
      setSignedUpOpportunityIds(new Set());
    }
  }

  async function loadOpportunitySignups(opportunityId) {
    const oppId = opportunityId != null ? String(opportunityId).trim() : "";
    if (!oppId || !account_id) {
      setOpportunitySignups([]);
      return;
    }
    setSignupsLoading(true);
    try {
      const resp = await apiFetch(serverUrl + "/getVolunteerOpportunitySignups", {
        method: "POST",
        body: JSON.stringify(
          volunteerOpportunityRequestBody(volunteerSession, { volunteer_opportunity_id: oppId })
        ),
      });
      const data = resp.ok ? await resp.json() : [];
      setOpportunitySignups(Array.isArray(data) ? data : []);
    } catch {
      setOpportunitySignups([]);
    } finally {
      setSignupsLoading(false);
    }
  }

  function isSignedUpForOpportunity(opportunityId) {
    return signedUpOpportunityIds.has(String(opportunityId));
  }

  function canSignUpForOpportunity(obj) {
    if (!user_id || !obj?._id) return false;
    return normalizeStatus(obj.status) === "Open";
  }

  function isAvailableToRegister(obj) {
    return canSignUpForOpportunity(obj) && !isSignedUpForOpportunity(obj._id);
  }

  function isAlreadyRegistered(obj) {
    return isSignedUpForOpportunity(obj._id);
  }

  async function signUpForOpportunity(obj) {
    if (!obj?._id || !account_id || !user_id) {
      showAlertDialog("Sign up unavailable", "Account access is required to sign up.");
      return;
    }
    if (!canSignUpForOpportunity(obj)) {
      showAlertDialog("Sign up unavailable", "Sign up is only available for open opportunities.");
      return;
    }
    setSignupSubmitting(true);
    try {
      const resp = await apiFetch(serverUrl + "/registerVolunteerOpportunityByUserId", {
        method: "POST",
        body: JSON.stringify(
          volunteerOpportunityRequestBody(volunteerSession, {
            volunteer_opportunity_id: obj._id,
          })
        ),
      });
      const body = await resp.json().catch(() => null);
      if (!resp.ok) {
        const msg =
          (body && body.message) || (typeof body === "string" ? body : null) || "Sign up failed.";
        showAlertDialog("Sign up failed", msg);
        return;
      }
      setSignedUpOpportunityIds((prev) => new Set(prev).add(String(obj._id)));
      await getDocument();
      if (detailId && String(detailId) === String(obj._id)) {
        await loadOpportunitySignups(obj._id);
      }
      const label = (obj.name || "this opportunity").trim() || "this opportunity";
      showAlertDialog("Sign up confirmed", `You are signed up for "${label}".`);
    } catch (err) {
      setBannerError(getMessageForFetchError(err, "Sign up failed."));
    } finally {
      setSignupSubmitting(false);
    }
  }

  function cancelSignupForOpportunity(obj) {
    if (!obj?._id || !account_id || !user_id) {
      showAlertDialog("Cannot cancel signup", "Unable to cancel: missing account or signup info.");
      return;
    }
    const label = (obj.name || "this opportunity").trim() || "this opportunity";
    setConfirmDialog({
      open: true,
      title: "Cancel signup",
      mode: "confirm",
      message: `Cancel your signup for "${label}"? You can sign up again later if the opportunity is still open.`,
      onConfirm: async () => {
        setSignupSubmitting(true);
        try {
          const resp = await apiFetch(serverUrl + "/cancelVolunteerOpportunitySignup", {
            method: "POST",
            body: JSON.stringify(
              volunteerOpportunityRequestBody(volunteerSession, {
                volunteer_opportunity_id: obj._id,
              })
            ),
          });
          if (!resp.ok) {
            const text = await resp.text().catch(() => "");
            showAlertDialog("Cancel signup failed", text || "Cancel signup failed.");
            return;
          }
          setSignedUpOpportunityIds((prev) => {
            const next = new Set(prev);
            next.delete(String(obj._id));
            return next;
          });
          await getDocument();
          if (detailId && String(detailId) === String(obj._id)) {
            await loadOpportunitySignups(obj._id);
          }
        } catch (err) {
          setBannerError(getMessageForFetchError(err, "Cancel signup failed."));
        } finally {
          setSignupSubmitting(false);
        }
      },
    });
  }

  function renderSignupActionButton(obj, { variant = "button" } = {}) {
    if (!signupBrowseMode) return null;
    if (!canSignUpForOpportunity(obj) && !isSignedUpForOpportunity(obj._id)) return null;
    const signedUp = isSignedUpForOpportunity(obj._id);
    const title = signedUp ? "Cancel signup" : "Sign up";
    const onClick = (e) => {
      e.stopPropagation();
      if (signedUp) cancelSignupForOpportunity(obj);
      else signUpForOpportunity(obj);
    };
    if (variant === "icon") {
      return (
        <button
          type="button"
          className={signedUp ? "btn-link-danger" : "btn-link"}
          style={iconBtnStyle}
          title={title}
          aria-label={title}
          disabled={signupSubmitting}
          onClick={onClick}
        >
          {signedUp ? <IconCancel size={18} /> : <IconParticipants size={18} />}
        </button>
      );
    }
    const btnClass = signedUp ? "btn btn-outline-secondary" : "btn btn-primary";
    const style = variant === "compact" ? { fontSize: 12, padding: "2px 8px" } : undefined;
    return (
      <button
        type="button"
        className={btnClass}
        style={style}
        disabled={signupSubmitting}
        onClick={onClick}
      >
        {title}
      </button>
    );
  }

  /** Move the list time navigator so a record with this start date is visible. */
  function focusTimeNavOnOpportunityStart(startDatetimeOrYmd) {
    const ymd = getDueDatePart(startDatetimeOrYmd) || normalizeYmd(startDatetimeOrYmd);
    if (!ymd || timeNav.view === "all") return;
    timeNav.setFocusDateYmd(ymd);
  }

  function getDetailFormValues(obj) {
    const today = dayjs().format("YYYY-MM-DD");
    return {
      name: obj?.name || "",
      description: obj?.description != null ? String(obj.description) : "",
      eventDate: getDueDatePart(obj?.start_datetime) || today,
      startTime: getDueTimePart(obj?.start_datetime) || "09:00",
      endTime: getDueTimePart(obj?.end_datetime) || "10:00",
      repeat_rule: normalizeRepeatRule(obj?.repeat_rule ?? obj?.repeat, getDueDatePart(obj?.start_datetime)),
      location: obj?.location != null ? String(obj.location) : "",
      category: obj?.category || "",
      volunteers_needed:
        obj?.volunteers_needed != null && obj?.volunteers_needed !== ""
          ? String(getOpportunityVolunteersNeeded(obj))
          : "1",
      status: obj ? normalizeStatus(obj?.status) : "Open",
      event_id: obj?.event_id != null ? String(obj.event_id).trim() : "",
    };
  }

  function applyDetailFormValues(values) {
    setDetailName(values?.name || "");
    setDetailDescription(values?.description != null ? String(values.description) : "");
    setDetailEventDate(values?.eventDate || dayjs().format("YYYY-MM-DD"));
    setDetailStartTime(values?.startTime || "09:00");
    setDetailEndTime(values?.endTime || "10:00");
    setDetailRepeatRule(values?.repeat_rule != null ? values.repeat_rule : null);
    setDetailLocation(values?.location != null ? String(values.location) : "");
    setDetailCategory(values?.category || "");
    setDetailVolunteersNeeded(
      values?.volunteers_needed != null && String(values.volunteers_needed).trim() !== ""
        ? String(values.volunteers_needed)
        : "1"
    );
    setDetailStatus(normalizeStatus(values?.status));
    setDetailLinkedEventId(values?.event_id != null ? String(values.event_id).trim() : "");
  }

  function buildApiDatetimes(eventDate, startTime, endTime) {
    const dateStr = (eventDate || "").trim() || dayjs().format("YYYY-MM-DD");
    const dateCheck = validateDate(dateStr);
    if (!dateCheck.valid) return { ok: false, error: dateCheck.message || "Invalid date." };
    const startTimeStr = (startTime || "").trim() || "09:00";
    const endTimeStr = (endTime || "").trim() || "10:00";
    const rangeCheck = validateStartEndRange(
      `${dateStr}T${startTimeStr}`,
      `${dateStr}T${endTimeStr}`,
      { granularity: "time", allowEqual: true }
    );
    if (!rangeCheck.valid) return { ok: false, error: rangeCheck.message };
    return {
      ok: true,
      start_datetime: getDueDateForApi(dateStr, startTimeStr),
      end_datetime: getDueDateForApi(dateStr, endTimeStr),
    };
  }

  function openDetail(obj, mode = "view", options = {}) {
    if (signupBrowseMode && mode !== "view") return;
    setDetailMode(mode);
    setDetailTab(VO_DETAIL_TAB_DETAILS);
    if (!obj && mode === "new") {
      setDetailId(null);
      const base = {
        ...getDetailFormValues(null),
        startTime: workingHoursStart || "09:00",
        endTime: workingHoursEnd || "10:00",
      };
      const initialValues = options.initialValues ? { ...base, ...options.initialValues } : base;
      setDetailInitialValues(initialValues);
      applyDetailFormValues(initialValues);
      setDetailActiveField("name");
      return;
    }
    if (!obj) {
      setDetailMode("");
      return;
    }
    setDetailId(obj._id);
    const initialValues = getDetailFormValues(obj);
    setDetailInitialValues(initialValues);
    applyDetailFormValues(initialValues);
    setDetailActiveField(signupBrowseMode || mode !== "edit" ? "" : "name");
  }

  async function persistDetailChanges(fieldOverrides = {}) {
    const nameVal = (fieldOverrides.name ?? detailName ?? "").trim();
    if (!nameVal) return;
    const categoryVal = (fieldOverrides.category ?? detailCategory ?? "").trim();
    if (!categoryVal) {
      setBannerError("Category is required.");
      return;
    }
    const dt = buildApiDatetimes(detailEventDate, detailStartTime, detailEndTime);
    if (!dt.ok) {
      setBannerError(dt.error);
      return;
    }

    const volunteersNeededParsed = parseOptionalInt(
      fieldOverrides.volunteers_needed ?? detailVolunteersNeeded,
      { min: 1, defaultIfEmpty: 1, fieldName: "Volunteers needed" }
    );
    if (!volunteersNeededParsed.ok) {
      setBannerError(volunteersNeededParsed.error);
      return;
    }

    const payload = {
      account_id,
      name: nameVal,
      description: (fieldOverrides.description ?? detailDescription ?? "").trim(),
      start_datetime: dt.start_datetime,
      end_datetime: dt.end_datetime,
      repeat_rule: normalizeRepeatRule(
        fieldOverrides.repeat_rule != null ? fieldOverrides.repeat_rule : detailRepeatRule,
        detailEventDate
      ),
      location: (fieldOverrides.location ?? detailLocation ?? "").trim(),
      category: categoryVal,
      volunteers_needed: volunteersNeededParsed.value,
      status:
        detailMode === "new" || !detailId
          ? "Open"
          : normalizeStatus(fieldOverrides.status ?? detailStatus),
      event_id: (fieldOverrides.event_id ?? detailLinkedEventId ?? "").trim(),
    };

    const isNew = detailMode === "new" || !detailId;
    const url = isNew ? serverUrl + "/createVolunteerOpportunity" : serverUrl + "/updateVolunteerOpportunity";
    const body = isNew
      ? volunteerOpportunityRequestBody(volunteerSession, { ...payload, created_by: user_id || "" })
      : volunteerOpportunityRequestBody(volunteerSession, { ...payload, _id: detailId });

    try {
      if (isNew) {
        const resp = await apiFetch(url, {
          method: "POST",
          body: JSON.stringify(body),
        });
        const data = await resp.json().catch(() => null);
        if (!resp.ok) {
          setBannerError((data && data.message) || (typeof data === "string" ? data : "Request failed."));
          return;
        }
        if (data && data._id) {
          setDetailId(data._id);
          setDetailMode("view");
          setDetailActiveField("");
          focusTimeNavOnOpportunityStart(data.start_datetime || detailEventDate);
        }
      } else {
        const resp = await apiFetch(url, {
          method: "POST",
          body: JSON.stringify(body),
        });
        const text = await resp.text();
        if (!resp.ok) {
          setBannerError(text || "Request failed.");
          return;
        }
      }
      await getDocument();
      setDetailInitialValues({
        name: nameVal,
        description: (detailDescription || "").trim(),
        eventDate: (detailEventDate || "").trim(),
        startTime: (detailStartTime || "").trim() || "09:00",
        endTime: (detailEndTime || "").trim() || "10:00",
        repeat_rule: normalizeRepeatRule(detailRepeatRule, detailEventDate),
        location: (detailLocation || "").trim(),
        category: categoryVal,
        volunteers_needed: String(volunteersNeededParsed.value),
        status: isNew ? "Open" : normalizeStatus(detailStatus),
        event_id: payload.event_id,
      });
      setDetailActiveField("");
    } catch (err) {
      setBannerError(getMessageForFetchError(err));
    }
  }

  async function closeOneOpportunity(obj) {
    if (!account_id || !obj?._id) return;
    const label = (obj.name || "this opportunity").trim() || "this opportunity";
    const displayLabel = label.length > 100 ? `${label.slice(0, 100)}…` : label;
    const startLine = formatOpportunityDatetime(obj.start_datetime);
    const endLine = formatOpportunityDatetime(obj.end_datetime);
    const _id = obj._id;
    let confirmMessage = `Close "${displayLabel}"?\n\nStart: ${startLine}\nEnd: ${endLine}`;
    try {
      const previewRes = await apiFetch(serverUrl + "/previewCloseVolunteerOpportunity", {
        method: "POST",
        body: JSON.stringify(volunteerOpportunityRequestBody(volunteerSession, { _id })),
      });
      if (previewRes.ok) {
        const p = await previewRes.json();
        if (p.willCreateNext) {
          confirmMessage += `\n\nA new open opportunity will be created (${getRepeatLabel(p.repeat_rule)}).`;
        }
      }
    } catch {
      confirmMessage = `Close "${displayLabel}"?\n\nStart: ${startLine}\nEnd: ${endLine}`;
    }
    setConfirmDialog({
      open: true,
      title: "Close opportunity",
      message: confirmMessage,
      onConfirm: async () => {
        setOpportunityStatusActionIds((prev) => new Set(prev).add(String(_id)));
        try {
          const response = await apiFetch(serverUrl + "/closeVolunteerOpportunity", {
            method: "POST",
            body: JSON.stringify(volunteerOpportunityRequestBody(volunteerSession, { _id })),
          });
          if (response.ok) {
            getDocument();
            syncDetailAfterStatusChange(_id, "Closed");
          } else {
            const ct = (response.headers.get("content-type") || "").toLowerCase();
            const body = ct.includes("application/json") ? await response.json().catch(() => null) : await response.text();
            const msg = typeof body === "string" ? body : body && body.message ? body.message : null;
            showAlertDialog("Close failed", msg || "Failed to close opportunity");
          }
        } catch (error) {
          setBannerError(getMessageForFetchError(error, "Failed to close opportunity"));
        } finally {
          setOpportunityStatusActionIds((prev) => {
            const next = new Set(prev);
            next.delete(String(_id));
            return next;
          });
        }
      },
    });
  }

  function reopenOneOpportunity(_id) {
    setConfirmDialog({
      open: true,
      title: "Reopen opportunity",
      message: "Reopen this opportunity?",
      onConfirm: async () => {
        setOpportunityStatusActionIds((prev) => new Set(prev).add(String(_id)));
        try {
          const response = await apiFetch(serverUrl + "/reopenVolunteerOpportunity", {
            method: "POST",
            body: JSON.stringify(volunteerOpportunityRequestBody(volunteerSession, { _id })),
          });
          if (response.ok) {
            getDocument();
            syncDetailAfterStatusChange(_id, "Open");
          } else {
            const ct = (response.headers.get("content-type") || "").toLowerCase();
            const body = ct.includes("application/json") ? await response.json().catch(() => null) : await response.text();
            const msg = typeof body === "string" ? body : body && body.message ? body.message : null;
            showAlertDialog("Reopen failed", msg || "Failed to reopen opportunity");
          }
        } catch (error) {
          setBannerError(getMessageForFetchError(error, "Failed to reopen opportunity"));
        } finally {
          setOpportunityStatusActionIds((prev) => {
            const next = new Set(prev);
            next.delete(String(_id));
            return next;
          });
        }
      },
    });
  }

  function duplicateDocument(currentObj) {
    const duplicateName = buildDuplicateLabel(currentObj.name, { fallback: "Opportunity" });
    const dt = buildApiDatetimes(
      getDueDatePart(currentObj.start_datetime),
      getDueTimePart(currentObj.start_datetime) || "09:00",
      getDueTimePart(currentObj.end_datetime) || "10:00"
    );
    if (!dt.ok) {
      setBannerError(dt.error);
      return;
    }
    apiFetch(serverUrl + "/createVolunteerOpportunity", {
      method: "POST",
      body: JSON.stringify(
        volunteerOpportunityRequestBody(volunteerSession, {
          created_by: user_id || "",
          name: duplicateName,
          description: (currentObj.description != null ? String(currentObj.description) : "").trim(),
          start_datetime: dt.start_datetime,
          end_datetime: dt.end_datetime,
          repeat_rule: currentObj.repeat_rule || null,
          location: (currentObj.location != null ? String(currentObj.location) : "").trim(),
          category: (currentObj.category != null ? String(currentObj.category) : "").trim(),
          volunteers_needed: getOpportunityVolunteersNeeded(currentObj),
          status: "Open",
          event_id: currentObj.event_id != null ? String(currentObj.event_id).trim() : "",
        })
      ),
    })
      .then(async (response) => {
        const data = await response.text();
        if (!response.ok) throw new Error(data || "Failed to duplicate opportunity");
        const displayLabel = duplicateName.trim() || "Duplicated opportunity";
        const footer = `Category: ${(currentObj.category || "—")}\nStatus: Open`;
        let created = null;
        try {
          created = JSON.parse(data);
        } catch {
          created = null;
        }
        const openCreated = () => {
          closeConfirmDialog();
          if (created && created._id) {
            openDetail(
              {
                _id: created._id,
                name: created.name != null ? String(created.name) : duplicateName,
                description: created.description,
                start_datetime: created.start_datetime,
                end_datetime: created.end_datetime,
                repeat_rule: created.repeat_rule,
                location: created.location,
                category: created.category,
                status: created.status,
                volunteers_needed: created.volunteers_needed,
                event_id: created.event_id,
              },
              "edit"
            );
          }
        };
        setConfirmDialog({
          open: true,
          title: "Opportunity duplicated",
          message:
            created && created._id ? (
              <DuplicateSuccessMessage linkLabel={displayLabel} onOpenDetail={openCreated} footer={footer} />
            ) : (
              `Created "${displayLabel}".\n\n${footer}`
            ),
          onConfirm: null,
        });
        getDocument();
      })
      .catch((err) => setBannerError(getMessageForFetchError(err)));
  }

  function deleteDocument(currentObj) {
    setConfirmDialog({
      open: true,
      title: "Delete volunteer opportunity",
      mode: "confirm",
      message: `Are you sure you want to delete "${currentObj.name || "this opportunity"}"?`,
      onConfirm: () => {
        apiFetch(serverUrl + "/deleteVolunteerOpportunity", {
          method: "POST",
          body: JSON.stringify(volunteerOpportunityRequestBody(volunteerSession, { _id: currentObj._id })),
        })
          .then((response) => response.text())
          .then((data) => {
            closeConfirmDialog();
            setConfirmDialog({ open: true, title: "Volunteer opportunity", message: data, mode: "alert", onConfirm: null });
            const newSelectedIds = new Set(selectedIds);
            newSelectedIds.delete(currentObj._id);
            setSelectedIds(newSelectedIds);
            getDocument();
          })
          .catch((err) => setBannerError(getMessageForFetchError(err)));
      },
    });
  }

  function parseRecordsFromCsvText(csvText) {
    const { headers, rows } = parseCsvTextToNormalizedTable(csvText);
    const nameIdx = findCsvColumnIndex(headers, ["name", "opportunity", "opportunity_name"]);
    const descIdx = findCsvColumnIndex(headers, ["description", "desc"]);
    const startIdx = findCsvColumnIndex(headers, ["start_datetime", "start", "start_date"]);
    const endIdx = findCsvColumnIndex(headers, ["end_datetime", "end", "end_date"]);
    const repeatIdx = findCsvColumnIndex(headers, ["repeat"]);
    const locationIdx = findCsvColumnIndex(headers, ["location"]);
    const categoryIdx = findCsvColumnIndex(headers, ["category"]);
    const volunteersNeededIdx = findCsvColumnIndex(headers, ["volunteers_needed", "volunteers needed", "volunteers"]);
    if (nameIdx === -1) throw new Error("CSV must contain a name column");
    if (startIdx === -1 || endIdx === -1) throw new Error("CSV must contain start_datetime and end_datetime columns");
    if (categoryIdx === -1) throw new Error("CSV must contain a category column");
    return rows.map((values) => ({
      name: values[nameIdx] ?? "",
      description: descIdx >= 0 && values[descIdx] !== undefined ? values[descIdx] : "",
      start_datetime: values[startIdx] ?? "",
      end_datetime: values[endIdx] ?? "",
      repeat: repeatIdx >= 0 && values[repeatIdx] !== undefined ? values[repeatIdx] : "0",
      location: locationIdx >= 0 && values[locationIdx] !== undefined ? values[locationIdx] : "",
      category: values[categoryIdx] ?? "",
      volunteers_needed:
        volunteersNeededIdx >= 0 && values[volunteersNeededIdx] !== undefined ? values[volunteersNeededIdx] : "",
    }));
  }

  async function importFromCSV(file) {
    if (!file) {
      setConfirmDialog({ open: true, title: "Import", message: "Please select a CSV file", mode: "alert", onConfirm: null });
      return;
    }
    const text = await file.text();
    await importFromCsvText(text);
  }

  async function importFromCsvText(text) {
    setImporting(true);
    try {
      const records = parseRecordsFromCsvText(text);
      if (records.length === 0) {
        setConfirmDialog({ open: true, title: "Import", message: "No valid records found in CSV file", mode: "alert", onConfirm: null });
        setImporting(false);
        return;
      }
      let successCount = 0;
      let errorCount = 0;
      const errors = [];
      for (let i = 0; i < records.length; i++) {
        const record = records[i];
        const rowNum = i + 2;
        if (!(record.name && String(record.name).trim())) {
          errorCount++;
          errors.push(`Row ${rowNum}: Missing name`);
          continue;
        }
        if (!(record.category && String(record.category).trim())) {
          errorCount++;
          errors.push(`Row ${rowNum}: Missing category`);
          continue;
        }
        const repeatRes = { ok: true, rule: normalizeRepeatRule(record.repeat_rule ?? record.repeat, getDueDatePart(record.start_datetime)) };
        if (!repeatRes.ok) {
          errorCount++;
          errors.push(`Row ${rowNum}: ${repeatRes.error}`);
          continue;
        }
        const volunteersNeededParsed = parseOptionalInt(record.volunteers_needed, {
          min: 1,
          defaultIfEmpty: 1,
          fieldName: "Volunteers needed",
        });
        if (!volunteersNeededParsed.ok) {
          errorCount++;
          errors.push(`Row ${rowNum}: ${volunteersNeededParsed.error}`);
          continue;
        }
        try {
          const response = await apiFetch(serverUrl + "/createVolunteerOpportunity", {
            method: "POST",
            body: JSON.stringify(
              volunteerOpportunityRequestBody(volunteerSession, {
              created_by: user_id || "",
              name: String(record.name).trim(),
              description: (record.description != null ? String(record.description) : "").trim(),
              start_datetime: String(record.start_datetime).trim(),
              end_datetime: String(record.end_datetime).trim(),
              repeat_rule: repeatRes.rule,
              location: (record.location != null ? String(record.location) : "").trim(),
              category: String(record.category).trim(),
              volunteers_needed: volunteersNeededParsed.value,
              status: "Open",
            })
            ),
          });
          const data = await response.text();
          if (response.ok) successCount++;
          else {
            errorCount++;
            errors.push(`Row ${rowNum}: ${data}`);
          }
        } catch (error) {
          errorCount++;
          errors.push(`Row ${rowNum}: ${getMessageForFetchError(error)}`);
        }
      }
      setConfirmDialog({
        open: true,
        title: "Import completed",
        message: formatBatchSummaryMessage("Import completed!", successCount, errorCount, errors),
        onConfirm: null,
        mode: "alert",
      });
      getDocument();
    } catch (error) {
      setBannerError(getMessageForFetchError(error, "Error importing CSV."));
    } finally {
      setImporting(false);
    }
  }

  function handleFileChange(e) {
    attachValidatedCsvFileImport(e, importFromCSV, (msg) => {
      setConfirmDialog({ open: true, title: "Import", message: msg, mode: "alert", onConfirm: null });
    });
  }

  function downloadSampleCSV() {
    downloadCsvTextFile("volunteer-opportunities-sample.csv", [
      "name,description,start_datetime,end_datetime,repeat,location,category,volunteers_needed",
      "Park cleanup,Help clean the community park,2026-06-01T09:00:00.000Z,2026-06-01T12:00:00.000Z,0,123 Main St,Environmental,3",
      "Food drive sorting,Sort donations at the food bank,2026-06-15T14:00:00.000Z,2026-06-15T17:00:00.000Z,7,456 Center Ave,Food Bank,1",
    ]);
  }

  function matchesVolunteerListFilters(obj) {
    if (
      !matchesVolunteerOpportunityScopeFilters(obj, {
        timeNav,
        eventId: eventIdFromUrl,
        statusFilter,
        category: categoryFilter,
        opportunityId: "",
      })
    ) {
      return false;
    }
    return matchesVolunteerOpportunitySearch(obj, searchTerm);
  }

  function navigateToParticipantsDrill(obj) {
    navigate(
      buildVolunteerParticipantsDrillFromOpportunities({
        opportunityId: obj?._id,
        eventId: eventIdFromUrl,
        categoryFilter,
        statusFilter,
        timeNav,
      })
    );
  }

  function renderSignupCountDrill(obj, { variant = "table" } = {}) {
    const count = getOpportunitySignupCount(obj);
    const label = variant === "card" ? `${count} signed up` : String(count);
    return (
      <button
        type="button"
        className="btn-link"
        style={
          variant === "card"
            ? { padding: 0, font: "inherit", color: "inherit", textDecoration: "underline" }
            : undefined
        }
        title="View signed-up volunteers"
        onClick={(e) => {
          e.stopPropagation();
          navigateToParticipantsDrill(obj);
        }}
      >
        {label}
      </button>
    );
  }

  const signupQuickFilterCounts = useMemo(() => {
    if (!signupBrowseMode) return { available: 0, registered: 0 };
    let available = 0;
    let registered = 0;
    for (const obj of currentObjs || []) {
      if (!matchesVolunteerListFilters(obj)) continue;
      if (isAvailableToRegister(obj)) available += 1;
      if (isAlreadyRegistered(obj)) registered += 1;
    }
    return { available, registered };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- counts follow list filters + signup state
  }, [
    signupBrowseMode,
    currentObjs,
    searchTerm,
    statusFilter,
    categoryFilter,
    eventIdFromUrl,
    timeNav.bounds,
    timeNav.view,
    signedUpOpportunityIds,
  ]);

  function getFilteredAndSortedData() {
    let filtered = (currentObjs || []).filter((obj) => {
      if (!matchesVolunteerListFilters(obj)) return false;
      if (signupBrowseMode && signupQuickFilter === "available" && !isAvailableToRegister(obj)) return false;
      if (signupBrowseMode && signupQuickFilter === "registered" && !isAlreadyRegistered(obj)) return false;
      return true;
    });
    return sortRowsByConfig(filtered, sortConfig, SORT_KEY_TYPES);
  }

  function getSelectedVisibleIds() {
    return intersectSelectedIdsWithRows(selectedIds, getFilteredAndSortedData());
  }

  async function deleteSelected() {
    const selectedVisibleIds = getSelectedVisibleIds();
    if (selectedVisibleIds.size === 0) {
      setConfirmDialog({
        open: true,
        title: "Delete opportunities",
        message: "Please select at least one record to delete",
        mode: "alert",
        onConfirm: null,
      });
      return;
    }
    setConfirmDialog({
      open: true,
      title: "Delete opportunities",
      mode: "confirm",
      message: `Are you sure you want to delete ${selectedVisibleIds.size} record(s)?`,
      onConfirm: async () => {
        setDeleting(true);
        const idsToDelete = Array.from(selectedVisibleIds);
        const { successCount, errorCount, errors } = await bulkSequentialDelete(
          idsToDelete,
          async (_id) => {
            const response = await apiFetch(serverUrl + "/deleteVolunteerOpportunity", {
              method: "POST",
              body: JSON.stringify(volunteerOpportunityRequestBody(volunteerSession, { _id })),
            });
            const data = await response.text();
            return { ok: response.ok, body: data };
          },
          getMessageForFetchError
        );
        setConfirmDialog({
          open: true,
          title: "Delete completed",
          message: formatBatchSummaryMessage("Delete completed!", successCount, errorCount, errors),
          onConfirm: null,
          mode: "alert",
        });
        setSelectedIds(new Set());
        getDocument();
        setDeleting(false);
      },
    });
  }

  const detailCurrentValues = {
    name: (detailName || "").trim(),
    description: (detailDescription || "").trim(),
    eventDate: (detailEventDate || "").trim(),
    startTime: (detailStartTime || "").trim() || "09:00",
    endTime: (detailEndTime || "").trim() || "10:00",
    repeat_rule: normalizeRepeatRule(detailRepeatRule, detailEventDate),
    location: (detailLocation || "").trim(),
    category: (detailCategory || "").trim(),
    volunteers_needed: (detailVolunteersNeeded || "").trim() || "1",
    status: normalizeStatus(detailStatus),
    event_id: (detailLinkedEventId || "").trim(),
  };
  const detailHasChanges =
    !!detailInitialValues &&
    Object.keys(detailCurrentValues).some((k) => {
      if (k === "repeat_rule") {
        return JSON.stringify(detailCurrentValues[k]) !== JSON.stringify(detailInitialValues[k]);
      }
      return detailCurrentValues[k] !== detailInitialValues[k];
    });
  const {
    isSaving: detailSaving,
    handleFieldBlur: handleDetailFieldBlur,
    handleFieldCommitKeyDown: handleDetailFieldCommitKeyDown,
    saveIfNeeded: saveDetailIfNeeded,
  } = useBlurAutoSave({
    hasChanges: detailHasChanges,
    saveFn: persistDetailChanges,
    onExitEdit: () => setDetailActiveField(""),
  });
  const detailAutoSaveEnabled = detailMode !== "new";
  const detailFieldBlurHandler = detailAutoSaveEnabled ? handleDetailFieldBlur : undefined;
  const handleCategoryFieldCommit = detailAutoSaveEnabled
    ? (selected) => {
        const fromPick = typeof selected === "string";
        const nextCategory = fromPick ? selected.trim() : (detailCategory || "").trim();
        if (fromPick) setDetailCategory(nextCategory);
        void (async () => {
          await persistDetailChanges({ category: nextCategory });
          setDetailActiveField("");
        })();
      }
    : undefined;
  const detailFieldCommitKeyDownHandler = detailAutoSaveEnabled ? handleDetailFieldCommitKeyDown : undefined;
  const filteredRows = getFilteredAndSortedData();
  const selectedVisibleIds = intersectSelectedIdsWithRows(selectedIds, filteredRows);
  const selectedVisibleCount = selectedVisibleIds.size;
  const selectedCountSuffix = formatSelectedCountSuffix(selectedVisibleCount);
  const hasDrillFilters =
    drillSourceFromUrl === "volunteer_impact_dashboard" &&
    (categoryFromUrl || statusFromUrl || navViewFromUrl || navDateFromUrl);
  const canViewLinkedEvent = hasPermission && hasPermission("menu.events");

  const detailPanelOpen =
    detailMode === "view" || (!signupBrowseMode && (detailMode === "edit" || detailMode === "new"));

  const pageSubtitleBase = signupBrowseMode
    ? "Browse volunteer opportunities and sign up or cancel your registration."
    : "Manage volunteer opportunities for your organization.";
  const categoryDrillLabel = (categoryFromUrl || categoryFilter || "").trim();
  const statusDrillLabel = formatVolunteerDrillStatusLabel(statusFromUrl, statusFilter);

  return (
    <EntityPageScaffold
      title={signupBrowseMode ? "Volunteer Signup" : "Volunteer Opportunities"}
      titleTabs={
        <VolunteerSectionTabs
          activeTab={signupBrowseMode ? VOLUNTEER_TAB_SIGNUP : VOLUNTEER_TAB_OPPORTUNITIES}
        />
      }
      subtitle={
        hasDrillFilters || eventIdFromUrl ? (
          <span style={{ display: "block" }}>
            {pageSubtitleBase}
            <span
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                flexWrap: "wrap",
                marginTop: 6,
                fontSize: 12,
                color: "#495057",
              }}
            >
              {hasDrillFilters ? <span>Filter from Volunteer Dashboard:</span> : null}
              {eventIdFromUrl ? (
                <span style={volunteerDrillChipStyle}>
                  Linked to event{eventFilterName || linkedEventName ? `: ${eventFilterName || linkedEventName}` : ""}
                </span>
              ) : null}
              {categoryDrillLabel ? (
                <span style={volunteerDrillChipStyle}>Category: {categoryDrillLabel}</span>
              ) : null}
              {statusDrillLabel ? (
                <span style={volunteerDrillChipStyle}>Status: {statusDrillLabel}</span>
              ) : null}
              {timeNav.title ? <span style={volunteerDrillChipStyle}>Period: {timeNav.title}</span> : null}
              {hasDrillFilters || eventIdFromUrl ? (
                <button type="button" onClick={clearDashboardDrillFilters} style={volunteerDrillClearBtnStyle}>
                  Clear filter
                </button>
              ) : null}
            </span>
          </span>
        ) : (
          pageSubtitleBase
        )
      }
      bannerError={bannerError}
      onDismissBanner={() => setBannerError("")}
      confirmDialog={confirmDialog}
      onConfirmModal={() => {
        const fn = confirmDialog.onConfirm;
        closeConfirmDialog();
        if (fn) fn();
      }}
      onCancelModal={closeConfirmDialog}
      pageTopAfterModal={
        <>
          {detailPanelOpen && (
            <div className="detail-modal-overlay">
              <div
                role="button"
                tabIndex={0}
                className="detail-modal-backdrop"
                onClick={() => setDetailMode("")}
                onKeyDown={(e) => e.key === "Escape" && setDetailMode("")}
                aria-label="Close"
              />
              <div className="panel detail-modal-panel">
                <div className="panel-row" style={{ justifyContent: "space-between", alignItems: "center", borderBottom: "1px solid #dee2e6", padding: "12px 16px" }}>
                  <h3 className="panel-title" style={{ margin: 0, fontSize: "1.1rem" }}>
                    {detailMode === "new" ? "New volunteer opportunity" : "Opportunity details"}
                  </h3>
                  <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                    {!signupBrowseMode && detailMode === "new" && (
                      <button
                        type="button"
                        className="btn btn-primary"
                        disabled={!detailCurrentValues.name || !detailCurrentValues.category || detailSaving}
                        onClick={async () => {
                          await saveDetailIfNeeded();
                        }}
                      >
                        Create
                      </button>
                    )}
                    {detailMode !== "new" && detailId
                      ? renderSignupActionButton(
                          {
                            _id: detailId,
                            name: detailName,
                            status: detailStatus,
                          },
                          { variant: "compact" }
                        )
                      : null}
                    {!signupBrowseMode ? (
                      <DetailSaveStatus isSaving={detailSaving} hasChanges={detailHasChanges} />
                    ) : null}
                    {!signupBrowseMode && detailMode !== "new" && detailId ? (
                      normalizeStatus(detailStatus) === "Closed" ? (
                        <button
                          type="button"
                          onClick={() => reopenOneOpportunity(detailId)}
                          className="btn-link"
                          style={iconBtnStyle}
                          title="Reopen"
                          disabled={opportunityStatusActionIds.has(String(detailId))}
                          aria-disabled={opportunityStatusActionIds.has(String(detailId)) ? "true" : "false"}
                          aria-label="Reopen opportunity"
                        >
                          <IconReopen size={18} />
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={() => {
                            const record = getOpportunityForStatusAction(detailId);
                            if (record) closeOneOpportunity(record);
                          }}
                          className="btn-link-muted"
                          style={iconBtnStyle}
                          title="Mark Complete"
                          disabled={opportunityStatusActionIds.has(String(detailId))}
                          aria-disabled={opportunityStatusActionIds.has(String(detailId)) ? "true" : "false"}
                          aria-label="Mark opportunity complete"
                        >
                          <IconClose size={18} />
                        </button>
                      )
                    ) : null}
                    <button
                      type="button"
                      className="btn-link btn-link-muted"
                      onClick={() => setDetailMode("")}
                      style={{ fontSize: "18px", lineHeight: 1, padding: "4px 8px" }}
                      aria-label="Close"
                    >
                      ×
                    </button>
                  </div>
                </div>
                {!signupBrowseMode && detailMode !== "new" && detailId ? (
                  <VolunteerOpportunityDetailTabBar
                    tab={detailTab}
                    onTabChange={setDetailTab}
                    volunteerCount={opportunitySignups.length}
                  />
                ) : null}
                {signupBrowseMode && detailMode === "view" && detailId ? (
                  <SignupBrowseDetailFields
                    detailName={detailName}
                    detailCategory={detailCategory}
                    detailStatus={detailStatus}
                    detailRepeatRule={detailRepeatRule}
                    detailEventDate={detailEventDate}
                    detailStartTime={detailStartTime}
                    detailEndTime={detailEndTime}
                    detailLocation={detailLocation}
                    detailDescription={detailDescription}
                    detailVolunteersNeeded={detailVolunteersNeeded}
                    linkedEventName={linkedEventName}
                    detailLinkedEventId={detailLinkedEventId}
                    canViewLinkedEvent={canViewLinkedEvent}
                    navigate={navigate}
                  />
                ) : detailTab === VO_DETAIL_TAB_VOLUNTEERS && detailMode !== "new" && detailId ? (
                  <VolunteerOpportunitySignupsTab signups={opportunitySignups} loading={signupsLoading} />
                ) : (
                <div style={{ padding: "12px 16px" }}>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: "12px 16px" }}>
                    <div style={{ display: "flex", flexDirection: "column" }}>
                      <label className="panel-label">Name</label>
                      {detailMode === "new" || detailActiveField === "name" ? (
                        <input
                          autoFocus={detailMode === "new" || detailActiveField === "name"}
                          type="text"
                          value={detailName}
                          onChange={(e) => setDetailName(e.target.value)}
                          onBlur={detailFieldBlurHandler}
                          onKeyDown={detailFieldCommitKeyDownHandler}
                          className="input input-full"
                        />
                      ) : (
                        <button type="button" onClick={() => setDetailActiveField("name")} className="btn-link" style={{ textAlign: "left", width: "100%", padding: "6px 0", color: "#212529", textDecoration: "none", fontWeight: 500 }}>
                          {detailName || "—"}
                        </button>
                      )}
                    </div>
                    <div style={{ display: "flex", flexDirection: "column" }}>
                      <label className="panel-label">Category</label>
                      {detailMode === "new" || detailActiveField === "category" ? (
                        <InlinePickInput
                          autoFocus={detailActiveField === "category"}
                          value={detailCategory}
                          onChange={setDetailCategory}
                          onBlur={handleCategoryFieldCommit}
                          onKeyDown={detailFieldCommitKeyDownHandler}
                          onOptionSelect={handleCategoryFieldCommit}
                          options={getCategories()}
                          placeholder="Category or pick existing"
                        />
                      ) : (
                        <button type="button" onClick={() => setDetailActiveField("category")} className="btn-link" style={{ textAlign: "left", width: "100%", padding: "6px 0", color: "#212529", textDecoration: "none", fontWeight: 500 }}>
                          {detailCategory || "—"}
                        </button>
                      )}
                    </div>
                    <div style={{ display: "flex", flexDirection: "column" }}>
                      <label className="panel-label">Volunteers needed</label>
                      {detailMode === "new" || detailActiveField === "volunteers_needed" ? (
                        <input
                          type="text"
                          inputMode="numeric"
                          value={detailVolunteersNeeded}
                          onChange={(e) => setDetailVolunteersNeeded(e.target.value)}
                          onBlur={detailFieldBlurHandler}
                          onKeyDown={detailFieldCommitKeyDownHandler}
                          className="input input-full input-number-no-spin"
                          style={{ maxWidth: 120 }}
                        />
                      ) : (
                        <button
                          type="button"
                          onClick={() => setDetailActiveField("volunteers_needed")}
                          className="btn-link"
                          style={{ textAlign: "left", width: "100%", padding: "6px 0", color: "#212529", textDecoration: "none", fontWeight: 500 }}
                        >
                          {getOpportunityVolunteersNeeded({ volunteers_needed: detailVolunteersNeeded })}
                        </button>
                      )}
                    </div>
                    {detailMode !== "new" ? (
                      <div style={{ display: "flex", flexDirection: "column" }}>
                        <label className="panel-label">Status</label>
                        {detailActiveField === "status" ? (
                          <select
                            autoFocus
                            value={detailStatus}
                            onChange={(e) => setDetailStatus(e.target.value)}
                            onBlur={detailFieldBlurHandler}
                            onKeyDown={detailFieldCommitKeyDownHandler}
                            className="input input-full"
                          >
                            {VOLUNTEER_OPPORTUNITY_STATUS_OPTIONS.map((s) => (
                              <option key={s} value={s}>
                                {s}
                              </option>
                            ))}
                          </select>
                        ) : (
                          <button type="button" onClick={() => setDetailActiveField("status")} className="btn-link" style={{ textAlign: "left", width: "100%", padding: "6px 0", color: "#212529", textDecoration: "none", fontWeight: 500 }}>
                            {detailStatus || "—"}
                          </button>
                        )}
                      </div>
                    ) : null}
                    {detailMode !== "new" ? (
                      <div style={{ display: "flex", flexDirection: "column" }}>
                        <label className="panel-label">Repeat</label>
                        {detailActiveField === "repeat" ? (
                          <RepeatPatternPicker
                            anchorDate={detailEventDate}
                            value={detailRepeatRule}
                            onChange={setDetailRepeatRule}
                            onBlur={detailFieldBlurHandler}
                            autoFocus={detailActiveField === "repeat"}
                            compact
                            dropdownPortal
                            selectStyle={{ width: "100%", maxWidth: "148px", minWidth: "96px" }}
                          />
                        ) : (
                          <button type="button" onClick={() => setDetailActiveField("repeat")} className="btn-link" style={{ textAlign: "left", width: "100%", padding: "6px 0", color: "#212529", textDecoration: "none", fontWeight: 500 }}>
                            {getRepeatLabel(detailRepeatRule)}
                          </button>
                        )}
                      </div>
                    ) : null}
                    <DetailDateTimeFields
                      detailMode={detailMode}
                      detailActiveField={detailActiveField}
                      setDetailActiveField={setDetailActiveField}
                      detailEventDate={detailEventDate}
                      setDetailEventDate={setDetailEventDate}
                      detailStartTime={detailStartTime}
                      setDetailStartTime={setDetailStartTime}
                      detailEndTime={detailEndTime}
                      setDetailEndTime={setDetailEndTime}
                      detailRepeatRule={detailRepeatRule}
                      setDetailRepeatRule={setDetailRepeatRule}
                      opportunityTimeOptions={opportunityTimeOptions}
                      detailDateInputRef={detailDateInputRef}
                      detailFieldBlurHandler={detailFieldBlurHandler}
                      detailFieldCommitKeyDownHandler={detailFieldCommitKeyDownHandler}
                    />
                    {(detailLinkedEventId || "").trim() && canViewLinkedEvent ? (
                      <div style={{ gridColumn: "1 / span 2", display: "flex", flexDirection: "column" }}>
                        <label className="panel-label">Linked event</label>
                        <button
                          type="button"
                          className="btn-link"
                          style={{ textAlign: "left", width: "100%", padding: "6px 0", color: "#0d6efd", textDecoration: "none", fontWeight: 500 }}
                          onClick={() =>
                            navigate(
                              buildEventsManagementPath({
                                tab: EVENT_MGMT_TAB_EVENTS,
                                detail: (detailLinkedEventId || "").trim(),
                              })
                            )
                          }
                        >
                          {linkedEventName || "View event"}
                        </button>
                      </div>
                    ) : null}
                    <div style={{ gridColumn: "1 / span 2", display: "flex", flexDirection: "column" }}>
                      <label className="panel-label">Location</label>
                      {detailMode === "new" || detailActiveField === "location" ? (
                        <input
                          type="text"
                          value={detailLocation}
                          onChange={(e) => setDetailLocation(e.target.value)}
                          onBlur={detailFieldBlurHandler}
                          onKeyDown={detailFieldCommitKeyDownHandler}
                          className="input input-full"
                          placeholder="Address or conference link"
                        />
                      ) : (
                        <button type="button" onClick={() => setDetailActiveField("location")} className="btn-link" style={{ textAlign: "left", width: "100%", padding: "6px 0", color: "#212529", textDecoration: "none", fontWeight: 500 }}>
                          {detailLocation || "—"}
                        </button>
                      )}
                    </div>
                    <div style={{ gridColumn: "1 / span 2", display: "flex", flexDirection: "column" }}>
                      <label className="panel-label">Description</label>
                      {detailMode === "new" || detailActiveField === "description" ? (
                        <textarea
                          value={detailDescription}
                          onChange={(e) => setDetailDescription(e.target.value)}
                          onBlur={detailFieldBlurHandler}
                          onKeyDown={detailFieldCommitKeyDownHandler}
                          rows={3}
                          className="input input-full"
                        />
                      ) : (
                        <button type="button" onClick={() => setDetailActiveField("description")} className="btn-link" style={{ textAlign: "left", width: "100%", padding: "6px 0", color: "#212529", textDecoration: "none", minHeight: 48, whiteSpace: "pre-wrap" }}>
                          {detailDescription || "—"}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
                )}
              </div>
            </div>
          )}

          {!signupBrowseMode ? (
            <ImportCsvPanel
              expanded={importPanelExpanded}
              fileInputId="volunteer-opportunity-csv-import"
              importing={importing}
              columnHint="Columns: name, category, start_datetime, end_datetime (required). Optional: description, repeat, location, volunteers_needed. Imports are created as Open."
              onDownloadSample={downloadSampleCSV}
              onFileChange={handleFileChange}
              onImportText={importFromCsvText}
            />
          ) : null}

          <CrudListToolbar
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            searchPlaceholder="Search name, category, location, description…"
            onClearSearch={() => setSearchTerm("")}
            middleSlot={
              <>
                <span className="toolbar-label">
                  <label htmlFor="vo-status-filter">Status:</label>
                </span>
                <select id="vo-status-filter" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="input input-min-130">
                  <option value="">All</option>
                  {VOLUNTEER_OPPORTUNITY_STATUS_OPTIONS.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
                <span className="toolbar-label">
                  <label htmlFor="vo-category-filter">Category:</label>
                </span>
                <select id="vo-category-filter" value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)} className="input input-min-130">
                  <option value="">All</option>
                  {getCategories().map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
                {signupBrowseMode ? (
                  <>
                    <button
                      type="button"
                      className={`btn btn-sm ${signupQuickFilter === "available" ? "btn-primary-outline active" : "btn-secondary"}`}
                      onClick={() =>
                        setSignupQuickFilter((prev) => (prev === "available" ? "" : "available"))
                      }
                      aria-pressed={signupQuickFilter === "available"}
                    >
                      Available to register [{signupQuickFilterCounts.available}]
                    </button>
                    <button
                      type="button"
                      className={`btn btn-sm ${signupQuickFilter === "registered" ? "btn-primary-outline active" : "btn-secondary"}`}
                      onClick={() =>
                        setSignupQuickFilter((prev) => (prev === "registered" ? "" : "registered"))
                      }
                      aria-pressed={signupQuickFilter === "registered"}
                    >
                      Already registered [{signupQuickFilterCounts.registered}]
                    </button>
                  </>
                ) : null}
                <span className="toolbar-divider" />
                <GlobalTimeNavigatorBar
                  variant="inline"
                  title={timeNav.title}
                  view={timeNav.view}
                  onViewChange={timeNav.setView}
                  onToday={timeNav.goToday}
                  onPrev={timeNav.movePrev}
                  onNext={timeNav.moveNext}
                />
              </>
            }
            showCsvImport={!signupBrowseMode}
            showNew={!signupBrowseMode}
            showDelete={!signupBrowseMode}
            importPanelExpanded={importPanelExpanded}
            onToggleImport={() => setImportPanelExpanded(!importPanelExpanded)}
            onNew={() => {
              openDetail(null, "new");
              setImportPanelExpanded(false);
            }}
            onDeleteSelected={deleteSelected}
            deleteDisabled={selectedVisibleCount === 0 || deleting}
            deleteCountSuffix={selectedCountSuffix}
            viewMode={viewMode}
            onViewModeChange={setViewMode}
          />
        </>
      }
      list={
        <div className="table-scroll">
          {viewMode === "table" && (
            <div className="table-wrapper">
              <table>
                <thead>
                  <tr>
                    {!signupBrowseMode ? (
                      <th className="text-center">
                        <input
                          type="checkbox"
                          checked={filteredRows.length > 0 && filteredRows.every((obj) => selectedIds.has(obj._id))}
                          onChange={(e) => handleSelectAll(e, getFilteredAndSortedData)}
                          disabled={filteredRows.length === 0}
                        />
                      </th>
                    ) : null}
                    <th className="sortable" onClick={() => handleSort("name")}>
                      Name
                    </th>
                    <th className="sortable" onClick={() => handleSort("category")}>
                      Category
                    </th>
                    <th className="sortable" onClick={() => handleSort("start_datetime")}>
                      Date &amp; time
                    </th>
                    <th>Location</th>
                    <th className="sortable" onClick={() => handleSort("repeat_rule")}>
                      Repeat
                    </th>
                    <th className="sortable" onClick={() => handleSort("status")}>
                      Status
                    </th>
                    <th className="sortable text-center" onClick={() => handleSort("volunteers_needed")}>
                      Volunteers needed
                    </th>
                    <th className="sortable text-center" onClick={() => handleSort("signup_count")}>
                      Signed up
                    </th>
                    <TableActionColumnHeader />
                  </tr>
                </thead>
                <tbody>
                  {filteredRows.map((currentObj) => (
                    <tr key={currentObj._id}>
                      {!signupBrowseMode ? (
                        <td className="text-center">
                          <input
                            type="checkbox"
                            checked={selectedIds.has(currentObj._id)}
                            onChange={(e) => handleSelectOne(currentObj._id, e.target.checked)}
                          />
                        </td>
                      ) : null}
                      <td>
                        <button type="button" className="btn-link" onClick={() => openDetail(currentObj, "view")}>
                          {currentObj.name}
                        </button>
                      </td>
                      <td>{currentObj.category || "—"}</td>
                      <td className="table-cell-notes" style={{ whiteSpace: "nowrap" }}>
                        {formatOpportunityDateTimeRange(currentObj.start_datetime, currentObj.end_datetime)}
                      </td>
                      <td className="table-cell-notes">{currentObj.location || "—"}</td>
                      <td>{getRepeatLabel(currentObj.repeat_rule)}</td>
                      <td>{normalizeStatus(currentObj.status)}</td>
                      <td className="text-center">{getOpportunityVolunteersNeeded(currentObj)}</td>
                      <td className="text-center">{renderSignupCountDrill(currentObj)}</td>
                      <td className="text-center">
                        <RowActionIcons>
                          {renderSignupActionButton(currentObj, { variant: signupBrowseMode ? "compact" : "icon" })}
                          {!signupBrowseMode ? (
                            <>
                              <button type="button" onClick={() => openDetail(currentObj, "edit")} className="btn-link" style={iconBtnStyle} title="Edit">
                                <IconEdit size={18} />
                              </button>
                              <button type="button" onClick={() => duplicateDocument(currentObj)} className="btn-link" style={iconBtnStyle} title="Duplicate">
                                <IconDuplicate size={18} />
                              </button>
                              {normalizeStatus(currentObj.status) === "Closed" ? (
                                <button
                                  type="button"
                                  onClick={() => reopenOneOpportunity(currentObj._id)}
                                  className="btn-link"
                                  style={iconBtnStyle}
                                  title="Reopen"
                                  disabled={opportunityStatusActionIds.has(String(currentObj._id))}
                                  aria-disabled={opportunityStatusActionIds.has(String(currentObj._id)) ? "true" : "false"}
                                >
                                  <IconReopen size={18} />
                                </button>
                              ) : (
                                <button
                                  type="button"
                                  onClick={() => closeOneOpportunity(currentObj)}
                                  className="btn-link-muted"
                                  style={iconBtnStyle}
                                  title="Mark Complete"
                                  disabled={opportunityStatusActionIds.has(String(currentObj._id))}
                                  aria-disabled={opportunityStatusActionIds.has(String(currentObj._id)) ? "true" : "false"}
                                >
                                  <IconClose size={18} />
                                </button>
                              )}
                              <button type="button" onClick={() => deleteDocument(currentObj)} className="btn-link-danger" style={iconBtnStyle} title="Delete">
                                <IconDelete size={18} />
                              </button>
                            </>
                          ) : null}
                        </RowActionIcons>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {viewMode === "card" && (
            <div className="cards-wrapper">
              {filteredRows.map((o) => (
                <div key={o._id} className={getOpportunityCardClass(o.status)}>
                  <div className="card-header">
                    {!signupBrowseMode ? (
                      <input type="checkbox" checked={selectedIds.has(o._id)} onChange={(e) => handleSelectOne(o._id, e.target.checked)} />
                    ) : null}
                    <div className="card-header-content">
                      <button type="button" className="btn-link card-title" onClick={() => openDetail(o, "view")}>
                        {o.name || "—"}
                      </button>
                      {o.category && <div className="panel-hint">{o.category}</div>}
                    </div>
                  </div>
                  <div className="card-meta-muted">
                    {formatOpportunityDateTimeRange(o.start_datetime, o.end_datetime)}
                  </div>
                  <div className="card-meta-muted">
                    {normalizeStatus(o.status)} · {getRepeatLabel(o.repeat_rule)} · {getOpportunityVolunteersNeeded(o)} volunteers needed · {renderSignupCountDrill(o, { variant: "card" })}
                  </div>
                  {o.location && <div className="panel-hint card-notes">{o.location}</div>}
                  {o.description && <div className="panel-hint card-notes">{o.description}</div>}
                  <div className="card-footer">
                    {renderSignupActionButton(o, { variant: "compact" })}
                    {!signupBrowseMode ? (
                      <RowActionIcons variant="inline">
                        <button type="button" onClick={() => openDetail(o, "edit")} className="btn-link" style={iconBtnStyle} title="Edit">
                          <IconEdit size={18} />
                        </button>
                        <button type="button" onClick={() => duplicateDocument(o)} className="btn-link" style={iconBtnStyle} title="Duplicate">
                          <IconDuplicate size={18} />
                        </button>
                        {normalizeStatus(o.status) === "Closed" ? (
                          <button
                            type="button"
                            onClick={() => reopenOneOpportunity(o._id)}
                            className="btn-link"
                            style={iconBtnStyle}
                            title="Reopen"
                            disabled={opportunityStatusActionIds.has(String(o._id))}
                            aria-disabled={opportunityStatusActionIds.has(String(o._id)) ? "true" : "false"}
                          >
                            <IconReopen size={18} />
                          </button>
                        ) : (
                          <button
                            type="button"
                            onClick={() => closeOneOpportunity(o)}
                            className="btn-link-muted"
                            style={iconBtnStyle}
                            title="Mark Complete"
                            disabled={opportunityStatusActionIds.has(String(o._id))}
                            aria-disabled={opportunityStatusActionIds.has(String(o._id)) ? "true" : "false"}
                          >
                            <IconClose size={18} />
                          </button>
                        )}
                        <button type="button" onClick={() => deleteDocument(o)} className="btn-link-danger" style={iconBtnStyle} title="Delete">
                          <IconDelete size={18} />
                        </button>
                      </RowActionIcons>
                    ) : null}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      }
    />
  );
}

export function VolunteerSignupBrowse() {
  return <VolunteerOpportunities signupBrowseMode />;
}

export default VolunteerOpportunities;
